import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Modal, Radio, Select, Space, Spin, Tooltip, Typography, notification } from 'antd';
import { CopyOutlined, EditOutlined, FilePdfOutlined, PaperClipOutlined, PrinterOutlined, ReloadOutlined } from '@ant-design/icons';
import type { FieldMeta, TableContext } from '../../api/types';
import type { ContractTemplate, ContractRenderData, OverlayItem } from '../../contract/types';
import { buildSampleContractData, fetchContractData } from '../../contract/dataFetcher';
import { buildContractHtml } from '../../contract/renderer';
import { buildPdfFileName, exportToPdf } from '../../exporters/pdfExporter';
import { buildAttachmentFile, type AttachmentFormat } from '../../exporters/attachmentExporter';
import { getTableName, pickAttachmentField, rememberAttachmentField, saveFileToAttachmentCell } from '../../api/bitable';
import { printHtmlViaIframe } from '../../utils/printHtml';
import { renderHtmlOffscreen } from '../../utils/renderOffscreen';
import { useBilling } from '../../billing/hooks';
import { track } from '../../utils/track';
import { SignModal } from '../../sign/SignModal';
import { BatchPrintModal } from './BatchPrintModal';
import { resolveFileNameTemplate } from '../../contract/fileName';
import type { TableOpMessage } from '../../contract/canvasOps';

const PAPER_W_MM: Record<string, [number, number]> = {
  A4: [210, 297],
  A5: [148, 210],
  A3: [297, 420],
  Letter: [216, 279],
};
/** 成品 HTML 用 mm 排版，离屏 iframe 的宽度必须等于纸张宽度，
 *  否则截出来的画布比例不对，导出的字号和边距都会跟浏览器打印对不上。 */
function paperWidthMm(s: { paperSize: string; orientation: string }): number {
  const [w, h] = PAPER_W_MM[s.paperSize] ?? PAPER_W_MM.A4;
  return s.orientation === 'portrait' ? w : h;
}

interface Props {
  context: TableContext | null;
  recordId: string | null;
  template: ContractTemplate | null;
  fieldMetas: FieldMeta[];
  /** 编辑器里为 true：预览进入自由元素拖拽模式。 */
  overlayEdit?: boolean;
  /** 拖拽/缩放某个自由元素后的回调（editor 用它写回模板）。 */
  onOverlayPatch?: (
    id: string,
    patch: Pick<OverlayItem, 'xMm' | 'yMm' | 'wMm' | 'hMm'> & Partial<Pick<OverlayItem, 'anchor' | 'anchorSectionId'>>,
  ) => void;
  /** 画布里点选了某个区块。 */
  onSectionSelect?: (id: string) => void;
  /** 画布浮动工具条：上移/下移（dir=-1/1）。 */
  onSectionMove?: (id: string, dir: -1 | 1) => void;
  /** 画布浮动工具条：删除。 */
  onSectionDelete?: (id: string) => void;
  /** 画布上就地改完文字：把区块的原始 content 换成新文本。 */
  onSectionTextChange?: (id: string, text: string) => void;
  /** 画布上点了区块之间的「+」：在该位置插入新区块。 */
  onSectionInsert?: (index: number, parent?: { id: string; col: number } | null) => void;
  /** 画布切到「预览」：显示真实数据而不是字段胶囊。 */
  canvasPreview?: boolean;
  /** 画布上右键做的表格结构操作（合并/拆分/增删行列/均分列宽）。 */
  onTableOp?: (msg: TableOpMessage) => void;
  /** 画布上双击表格格子改完的文字。 */
  onCellText?: (id: string, r: number, c: number, text: string) => void;
  /** 画布上敲 / 选了块类型：插在该区块后面。 */
  /** 画布上敲 / 或在末尾空段落打字：在某区块后插块（'__end__' = 追加到末尾），text = 末尾打的字。 */
  onInsertAfter?: (afterId: string, blockType: string, text?: string) => void;
  /** 画布里按下 Cmd/Ctrl+Z：iframe 拿不到宿主的快捷键，由注入脚本转发。 */
  onUndo?: () => void;
  onRedo?: () => void;
  /** 点了画布上的字段胶囊换字段。 */
  onRebind?: (sectionId: string, from: string, to: string) => void;
  /** 区块工具条 / 右键：复制、更换类型。 */
  onSectionDuplicate?: (id: string) => void;
  onSectionConvert?: (id: string, blockType: string) => void;
  /** 画布上的格子选区变化（clear=true 表示选区作废）。 */
  onCellSelect?: (sel: { id: string; r0: number; c0: number; r1: number; c1: number } | null) => void;
  /** 让画布滚到某个区块并闪一下（绑定报告里点「未匹配」项）。n 变一次触发一次。 */
  locate?: { id: string; n: number } | null;
  /** 画布上拖动手柄换了顺序：把 id 挪到 targetId 的前/后。 */
  onSectionReorder?: (id: string, targetId: string, before: boolean) => void;
  /** 对照底图（只在编辑画布上垫着，不进打印和导出）。 */
  underlay?: { dataUrl: string; opacity: number } | null;
  /** 宿主面板选中的区块 id，画布同步高亮并滚动到位。 */
  selectedSectionId?: string | null;
  /** 全屏编辑器：无 SDK，直接用侧边栏传来的冻结数据快照。 */
  staticData?: ContractRenderData | null;
  /** 隐藏打印/导出/签名动作条（全屏编辑器专注编辑）。 */
  hideActions?: boolean;
}

export function ContractPreview({
  context,
  recordId,
  template,
  fieldMetas,
  overlayEdit,
  onOverlayPatch,
  onSectionSelect,
  onSectionMove,
  onSectionDelete,
  onSectionTextChange,
  onSectionInsert,
  onSectionReorder,
  underlay,
  canvasPreview,
  onTableOp,
  onCellText,
  onInsertAfter,
  onUndo,
  onRedo,
  onRebind,
  onSectionDuplicate,
  onSectionConvert,
  onCellSelect,
  locate,
  selectedSectionId,
  staticData,
  hideActions,
}: Props) {
  const { gate } = useBilling();
  const [data, setData] = useState<ContractRenderData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [signOpen, setSignOpen] = useState(false);
  const [batchOpen, setBatchOpen] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  /** 画布内容高度（由 iframe 里的脚本上报），iframe 跟着它长。 */
  const [frameH, setFrameH] = useState<number | null>(null);
  const tokenRef = useRef(0);
  /** 取数时刻 —— 附件的临时下载链接只活几分钟，输出前据此决定要不要重取。 */
  const dataAtRef = useRef(0);

  const refresh = useCallback(async () => {
    // 全屏模式：冻结快照，不走 SDK
    if (staticData !== undefined) {
      setData(staticData);
      return;
    }
    if (!template || (template.sections.length === 0 && !template.overlays?.length)) {
      setData(null);
      return;
    }
    // 没选记录 ≠ 没东西可看：先用示例数据把排版画出来，选中记录后自动换真值
    if (!context || !recordId) {
      setData(buildSampleContractData(fieldMetas));
      return;
    }
    const token = ++tokenRef.current;
    setLoading(true);
    setError(null);
    try {
      const result = await fetchContractData(context.tableId, recordId, template, fieldMetas, context.viewId);
      if (token !== tokenRef.current) return;
      dataAtRef.current = Date.now();
      setData(result);
      // 走到这里 = 有模板、有记录、数据取到了，预览是真内容
      track('record');
      track('preview');
    } catch (err) {
      if (token !== tokenRef.current) return;
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      if (token === tokenRef.current) setLoading(false);
    }
  }, [context, recordId, template, fieldMetas, staticData]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const html = useMemo(() => {
    if (!template || !data) return '';
    // 底图和字段胶囊都只在编辑态注入 —— buildOutputHtml 走的是另一条路，
    // 成品里不会有它们
    const forCanvas =
      overlayEdit
        ? { ...data, ...(underlay ? { underlay } : {}), fieldChips: !canvasPreview }
        : data;
    return buildContractHtml(template, forCanvas, overlayEdit ? 'edit' : 'preview');
  }, [template, data, overlayEdit, underlay, canvasPreview]);

  /** 成品 HTML（无编辑描边/签名占位框），打印/导出/签署都用它。 */
  const buildOutputHtml = useCallback(() => {
    if (!template || !data) return '';
    return buildContractHtml(template, data, 'output');
  }, [template, data]);

  /** 附件临时链接的保鲜期。飞书给的下载地址只活几分钟。 */
  const ATTACHMENT_TTL_MS = 3 * 60 * 1000;

  /**
   * 输出前拿一份「图还能加载」的成品 HTML。
   *
   * 预览停留久一点再点打印，附件图的临时链接已经过期，印出来是一排破图。
   * 只有真的用到附件时才重取，普通模板不额外花一次接口。
   */
  const buildFreshOutputHtml = useCallback(async (): Promise<string> => {
    if (!template) return '';
    const usesAttachments =
      !!data &&
      (Object.keys(data.attachments ?? {}).length > 0 ||
        Object.keys(data.mainAttachments ?? {}).length > 0 ||
        Object.values(data.linkedRecords ?? {}).some((t) =>
          t.records.some((r) => r.attachments && Object.keys(r.attachments).length > 0),
        ));
    const stale = Date.now() - dataAtRef.current > ATTACHMENT_TTL_MS;
    if (!usesAttachments || !stale || !context || !recordId || staticData !== undefined) {
      return buildOutputHtml();
    }
    try {
      const fresh = await fetchContractData(context.tableId, recordId, template, fieldMetas, context.viewId);
      dataAtRef.current = Date.now();
      setData(fresh);
      return buildContractHtml(template, fresh, 'output');
    } catch {
      // 重取失败就用手上这份，至少还能出图（可能有破图，但不该拦住打印）
      return buildOutputHtml();
    }
  }, [template, data, context, recordId, fieldMetas, staticData, buildOutputHtml]);

  // 编辑态：接收 iframe 里脚本发回的消息（元素坐标 / 区块点选与操作）
  useEffect(() => {
    if (!overlayEdit) return;
    const onMsg = (e: MessageEvent) => {
      const d = e.data;
      if (!d) return;
      if (d.type === 'pp-size') { const h = Number(d.h); if (h > 0) setFrameH(h); return; }
      if (d.type === 'pp-undo') { onUndo?.(); return; }
      if (d.type === 'pp-redo') { onRedo?.(); return; }
      // 插入点消息只有位置没有区块 id，不能被下面那道 id 门挡掉
      if (d.type === 'pp-section-insert') {
        onSectionInsert?.(Number(d.index), d.parentId ? { id: String(d.parentId), col: Number(d.col) || 0 } : null);
        return;
      }
      if (!d.id) return;
      if (d.type === 'pp-overlay-change' && onOverlayPatch) {
        onOverlayPatch(String(d.id), {
          xMm: Math.round(Number(d.xMm) * 10) / 10,
          yMm: Math.round(Number(d.yMm) * 10) / 10,
          wMm: Math.round(Number(d.wMm) * 10) / 10,
          hMm: Math.round(Number(d.hMm) * 10) / 10,
          // 拖动放下时画布会把章挂到落点下面的区块上
          ...(d.anchor === 'section' && d.anchorSectionId
            ? { anchor: 'section' as const, anchorSectionId: String(d.anchorSectionId) }
            : {}),
        });
      } else if (d.type === 'pp-table-op' && onTableOp) {
        onTableOp(d as unknown as TableOpMessage);
      } else if (d.type === 'pp-cell-text' && onCellText) {
        onCellText(String(d.id), Number(d.r), Number(d.c), String(d.text ?? ''));
      } else if (d.type === 'pp-rebind' && onRebind) {
        onRebind(String(d.id), String(d.from), String(d.to));
      } else if (d.type === 'pp-section-duplicate' && onSectionDuplicate) {
        onSectionDuplicate(String(d.id));
      } else if (d.type === 'pp-section-convert' && onSectionConvert) {
        onSectionConvert(String(d.id), String(d.blockType));
      } else if (d.type === 'pp-cell-select' && onCellSelect) {
        onCellSelect(d.clear ? null : { id: String(d.id), r0: Number(d.r0), c0: Number(d.c0), r1: Number(d.r1), c1: Number(d.c1) });
      } else if (d.type === 'pp-section-insert-after' && onInsertAfter) {
        onInsertAfter(String(d.id), String(d.blockType), typeof d.text === 'string' ? d.text : undefined);
      } else if (d.type === 'pp-section-reorder' && onSectionReorder) {
        onSectionReorder(String(d.id), String(d.targetId), !!d.before);
      } else if (d.type === 'pp-section-text' && onSectionTextChange) {
        onSectionTextChange(String(d.id), String(d.text ?? ''));
      } else if (d.type === 'pp-section-click' && onSectionSelect) {
        onSectionSelect(String(d.id));
      } else if (d.type === 'pp-section-move' && onSectionMove) {
        onSectionMove(String(d.id), d.dir === -1 ? -1 : 1);
      } else if (d.type === 'pp-section-delete' && onSectionDelete) {
        onSectionDelete(String(d.id));
      }
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, [overlayEdit, onOverlayPatch, onSectionSelect, onSectionMove, onSectionDelete, onSectionTextChange, onSectionInsert, onSectionReorder, onTableOp, onCellText, onInsertAfter, onUndo, onRedo, onSectionDuplicate, onSectionConvert, onCellSelect, onRebind]);

  // 绑定报告里点了某条「未匹配」：滚过去闪一下，让人一眼看到是哪儿
  useEffect(() => {
    if (!overlayEdit || !locate) return;
    const t = setTimeout(() => {
      iframeRef.current?.contentWindow?.postMessage({ type: 'pp-select', id: locate.id, flash: true }, '*');
    }, 250);
    return () => clearTimeout(t);
  }, [overlayEdit, locate]);

  // 宿主面板选中变化 → 通知画布高亮 + 滚动到位（延迟等 iframe 重载完成）
  useEffect(() => {
    if (!overlayEdit || !selectedSectionId) return;
    const t = setTimeout(() => {
      iframeRef.current?.contentWindow?.postMessage(
        { type: 'pp-select', id: selectedSectionId },
        '*',
      );
    }, 200);
    return () => clearTimeout(t);
  }, [overlayEdit, selectedSectionId, html]);

  // ── 存附件 ──────────────────────────────────────────
  const attachmentFields = useMemo(
    () => fieldMetas.filter((f) => f.type === 17),
    [fieldMetas],
  );
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveField, setSaveField] = useState<string>('');
  const [saveMode, setSaveMode] = useState<'append' | 'replace'>('append');
  const [saveFormat, setSaveFormat] = useState<AttachmentFormat>('pdf');
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    // 默认选第一个附件字段；表换了就重挑
    setSaveField((prev) =>
      prev && attachmentFields.some((f) => f.id === prev) ? prev : pickAttachmentField(context?.tableId, attachmentFields),
    );
  }, [attachmentFields, context?.tableId]);

  const handleSaveToField = useCallback(async () => {
    if (!context || !recordId || !template || !saveField) return;
    setSaving(true);
    try {
      const out = await buildFreshOutputHtml();
      if (!out) throw new Error('预览尚未就绪');
      if (!(await gate())) return;
      const custom = (template.settings.exportFileName || '').trim();
      const file = await buildAttachmentFile({
        html: out, settings: template.settings, format: saveFormat,
        name: custom && data ? resolveFileNameTemplate(custom, data) : template.name,
      });
      await saveFileToAttachmentCell(context.tableId, saveField, recordId, file, saveMode);
      rememberAttachmentField(context.tableId, saveField);
      track('attach_ok');
      notification.success({
        message: `已存入「${attachmentFields.find((f) => f.id === saveField)?.name ?? '附件'}」`,
        description: `${file.name} · 当前预览的这条记录`,
      });
      setSaveOpen(false);
    } catch (err) {
      track('attach_fail');
      notification.error({
        message: '存附件失败',
        description: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setSaving(false);
    }
  }, [context, recordId, template, saveField, saveMode, saveFormat, buildFreshOutputHtml, gate, data, attachmentFields]);

  const printingRef = useRef(false);
  const handlePrint = useCallback(async () => {
    // 双击「打印」会扣两次额度、弹两个打印对话框
    if (printingRef.current) return;
    printingRef.current = true;
    const out = await buildFreshOutputHtml();
    if (!out) {
      printingRef.current = false;
      notification.error({ message: '打印失败', description: '预览尚未就绪，请稍后再试。' });
      return;
    }
    try {
      if (!(await gate())) return;
      printHtmlViaIframe(out);
    } catch (err) {
      notification.error({
        message: '打印失败',
        description: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setTimeout(() => {
        printingRef.current = false;
      }, 800);
    }
  }, [buildFreshOutputHtml, gate]);

  const handleExportPdf = useCallback(async () => {
    if (!context || !template) return;
    const out = await buildFreshOutputHtml();
    if (!out) {
      notification.error({ message: 'PDF 导出失败', description: '预览尚未就绪' });
      return;
    }
    if (!(await gate())) return;
    setExporting(true);
    let cleanup: (() => void) | null = null;
    try {
      const rendered = await renderHtmlOffscreen(out, paperWidthMm(template.settings));
      cleanup = rendered.cleanup;
      const tableName = await getTableName(context.tableId).catch(() => '合同');
      // 自定义导出文件名（{字段名} 变量，用当前记录数据填充），否则默认表名
      const custom = (template.settings.exportFileName || '').trim();
      const fileName = custom && data
        ? `${resolveFileNameTemplate(custom, data)}.pdf`
        : buildPdfFileName(tableName);
      await exportToPdf({
        source: rendered.body,
        paperSize: template.settings.paperSize,
        orientation: template.settings.orientation,
        marginMm: template.settings.marginMm,
        fileName,
        continuous: template.settings.continuousPage,
      });
      notification.success({ message: 'PDF 已导出', description: fileName });
    } catch (err) {
      notification.error({ message: 'PDF 导出失败', description: err instanceof Error ? err.message : String(err) });
    } finally {
      cleanup?.();
      setExporting(false);
    }
  }, [context, template, buildOutputHtml, gate, data]);

  const noTemplate = !template || (template.sections.length === 0 && !(template.overlays?.length));
  // 示例态：画布照常渲染（占位符），但不允许打印/导出/发起签名出一份空文档
  const sample = !!data?.sampleMode;
  const ready = !!html && !loading && !sample;

  return (
    <div>
      <div style={{ marginBottom: 8, display: hideActions ? 'none' : undefined }}>
        <Space size={6} wrap>
          <Button
            type="primary"
            size="small"
            icon={<PrinterOutlined />}
            onClick={() => void handlePrint()}
            disabled={!ready}
          >
            打印
          </Button>
          <Button
            size="small"
            icon={<FilePdfOutlined />}
            onClick={() => void handleExportPdf()}
            disabled={!ready}
            loading={exporting}
          >
            导出 PDF
          </Button>
          <Button
            size="small"
            icon={<CopyOutlined />}
            onClick={() => setBatchOpen(true)}
            disabled={noTemplate || !context}
          >
            批量
          </Button>
          {/* 存附件：把成品写回这条记录的附件字段。单据最该待的地方就是记录上，
              而不是某个人的下载文件夹。没有附件字段时按钮禁用并说明原因。 */}
          <Tooltip
            title={
              attachmentFields.length === 0
                ? '这张表没有附件字段，先在表里加一个附件列'
                : '把生成的 PDF 或 Excel 存进这条记录的附件字段；多条一起存，点「批量」'
            }
          >
            <Button
              size="small"
              icon={<PaperClipOutlined />}
              onClick={() => setSaveOpen(true)}
              disabled={!ready || !context || !recordId || attachmentFields.length === 0}
            >
              存附件
            </Button>
          </Tooltip>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => setSignOpen(true)}
            disabled={!ready}
          >
            发起签名
          </Button>
          <Button
            size="small"
            icon={<ReloadOutlined />}
            onClick={() => void refresh()}
            loading={loading}
            disabled={noTemplate}
          >
            刷新
          </Button>
        </Space>
      </div>

      <Modal
        open={saveOpen}
        onCancel={saving ? undefined : () => setSaveOpen(false)}
        title="存入附件字段"
        okText="存入"
        confirmLoading={saving}
        onOk={() => void handleSaveToField()}
        width={360}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: 12 }}>
          <div>
            <div style={{ marginBottom: 4, color: 'var(--ink-2)' }}>文件格式</div>
            <Radio.Group
              size="small"
              value={saveFormat}
              onChange={(e) => setSaveFormat(e.target.value)}
              disabled={saving}
              optionType="button"
              options={[{ label: 'PDF', value: 'pdf' }, { label: 'Excel（.xlsx）', value: 'xlsx' }]}
            />
          </div>
          <div>
            <div style={{ marginBottom: 4, color: 'var(--ink-2)' }}>存到哪个附件字段</div>
            <Select
              size="small"
              style={{ width: '100%' }}
              value={saveField || undefined}
              onChange={setSaveField}
              disabled={saving}
              options={attachmentFields.map((f) => ({ label: f.name, value: f.id }))}
            />
          </div>
          <div>
            <div style={{ marginBottom: 4, color: 'var(--ink-2)' }}>写入方式</div>
            <Radio.Group
              size="small"
              value={saveMode}
              onChange={(e) => setSaveMode(e.target.value)}
              disabled={saving}
              optionType="button"
              options={[
                { label: '追加', value: 'append' },
                { label: '替换原附件', value: 'replace' },
              ]}
            />
          </div>
          <div style={{ color: 'var(--ink-3)', fontSize: 11 }}>
            {saveFormat === 'pdf'
              ? '存的是当前这条记录的 PDF，和「导出 PDF」内容一致。'
              : 'Excel 保留可编辑文字、表格和合并格。水印及复杂版式以 PDF 为准。'}
          </div>
        </div>
      </Modal>

      <SignModal
        open={signOpen}
        onClose={() => setSignOpen(false)}
        buildOutputHtml={buildFreshOutputHtml}
        docName={template?.name || '待签署文件'}
        tableId={context?.tableId || ''}
        recordId={recordId}
        paperWidthMm={template ? paperWidthMm(template.settings) : undefined}
      />

      {context && template && (
        <BatchPrintModal
          open={batchOpen}
          onClose={() => setBatchOpen(false)}
          context={context}
          template={template}
          fieldMetas={fieldMetas}
          gate={gate}
        />
      )}

      {error && <Alert type="error" showIcon message="数据加载失败" description={error} style={{ marginBottom: 8 }} />}

      {/* 只有「一个区块都没有」才收成紧凑空态；没选记录照样出画布（示例数据） */}
      {noTemplate ? (
        <div
          style={{
            border: '1px dashed var(--border, #d9d9d9)',
            borderRadius: 4,
            padding: '12px 14px',
            fontSize: 12,
            color: '#999',
          }}
        >
          模板还没有区块 — 添加「标题/抬头」等组件后，这里会实时出现预览。
        </div>
      ) : (
        <>
        {sample && (
          <div
            style={{
              border: '1px solid #f0d9c8',
              background: '#fdf6f1',
              borderRadius: 4,
              padding: '6px 10px',
              fontSize: 11.5,
              color: '#a55a33',
              marginBottom: 6,
            }}
          >
            未选中记录 · 下方为排版示例，字段值用〔字段名〕占位。在表格中点选一条记录即可填入真实数据并打印 / 导出。
          </div>
        )}
        <div
          style={{
            position: 'relative',
            // 像一张摊在桌面上的纸：阴影给出厚度，不再是一个带灰边的框。
            // 宽度按纸张来（A4 竖版 210mm ≈ 794px），别把整个中栏铺成白的
            boxShadow: '0 1px 2px rgba(50,30,0,.10), 0 12px 32px -12px rgba(50,30,0,.35)',
            borderRadius: 2,
            background: '#fff',
            maxWidth: 'calc(210mm + 2px)',
            margin: '0 auto',
          }}
        >
          {loading && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.7)', zIndex: 1 }}>
              <Spin />
            </div>
          )}
          {html ? (
            <iframe
              ref={iframeRef}
              srcDoc={html}
              title="合同预览"
              // 预览需要 zoom 自适应脚本、编辑态需要拖拽脚本
              sandbox="allow-same-origin allow-scripts"
              style={{ width: '100%', height: frameH ?? 400, minHeight: 200, border: 'none', background: '#fff', display: 'block', transition: 'height .12s ease-out' }}
            />
          ) : (
            <div style={{ padding: 16, textAlign: 'center', color: '#999', fontSize: 12 }}>
              {loading ? '正在加载数据…' : '暂无可预览内容'}
            </div>
          )}
        </div>
        </>
      )}

      <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 4 }}>
        所有数据处理都在浏览器内，不上传外部服务器。
      </Typography.Text>
    </div>
  );
}
